// filepath: apps/web/src/bootstrap/tenantConfig.ts
//
// FR-UI-CORE-2 step 2 of ARCHITECTURE_WEB.md §3: fetch the tenant
// config that drives the OIDC bootstrap.
//
// This module is a thin TanStack Query wrapper over GET
// /api/tenant-config. The cache key includes the tenantSlug
// (INV-1 — see FR-UI-CORE-1's pattern in useDefinition.ts).
//
// The query is unauthenticated (the SPA has no JWT yet) and uses
// the same `/api` base URL the rest of the SPA uses. The handler
// is on the public-path bypass in internal/auth/auth.go.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { TenantConfig } from "@/auth/types";

const TENANT_CONFIG_TIMEOUT_MS = 10_000;

/**
 * The TanStack Query cache key for the tenant config. The
 * tenantSlug is the second segment (INV-1, mirroring FR-UI-CORE-1's
// useDefinition pattern).
 */
export function tenantConfigQueryKey(slug: string): readonly unknown[] {
  return ["tenant-config", slug] as const;
}

/**
 * The error envelope returned by the bootstrap fetch. Each variant
 * maps to a dedicated AC-5 error screen.
 */
export interface TenantConfigError extends Error {
  /** HTTP status code (0 for network failures). */
  status: number;
  /** Error code from the backend (matches FR-UI-CORE-2 categorization). */
  code: string;
  /** True if the caller should retry with backoff. */
  retriable: boolean;
}

/**
 * Internal: build a typed error envelope.
 */
function makeTenantConfigError(
  message: string,
  status: number,
  code: string,
  retriable: boolean,
): TenantConfigError {
  const err = new Error(message) as TenantConfigError;
  err.status = status;
  err.code = code;
  err.retriable = retriable;
  return err;
}

/**
 * Fetches the tenant config from the backend. Pure function — no
 * React hooks, no TanStack Query. Useful for tests and for the
 * App composition root that wants to imperatively fetch.
 *
 * The fetch uses a 10-second timeout via Promise.race (rather than
 * AbortController) for jsdom compatibility — jsdom's fetch checks
 * `signal instanceof AbortSignal` against the realm's global
 * AbortSignal, and the Check fails across realms in tests.
 * Promise.race is functionally equivalent and avoids the cross-realm
 * check.
 */
export async function fetchTenantConfig(
  slug: string,
  apiBase = "/api",
): Promise<TenantConfig> {
  const url = `${apiBase}/tenant-config?slug=${encodeURIComponent(slug)}`;

  const fetchPromise = fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "include",
  });

  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(makeTenantConfigError("request timed out", 0, "timeout", true));
    }, TENANT_CONFIG_TIMEOUT_MS);
  });

  try {
    let response: Response;
    try {
      // Promise.race — the timeout rejects the Promise.race, but if
      // the fetch resolves first, we discard the timeout.
      response = await Promise.race([fetchPromise, timeoutPromise]);
    } catch (networkErr) {
      // The timeout error must take precedence over a generic
      // network error so the SPA can distinguish them.
      if (
        networkErr instanceof Error &&
        "code" in networkErr &&
        (networkErr as { code: string }).code === "timeout"
      ) {
        throw networkErr;
      }
      throw makeTenantConfigError(
        networkErr instanceof Error ? networkErr.message : "network error",
        0,
        "network",
        true,
      );
    }

    if (!response.ok) {
      const body = await safeReadJson(response);
      const code =
        (body as { code?: string } | null)?.code ?? `http_${response.status}`;
      const message =
        (body as { message?: string } | null)?.message ?? response.statusText;
      // 404 = tenant not found; 503 = tenant suspended; 500+ = server error.
      const retriable = response.status >= 500;
      throw makeTenantConfigError(message, response.status, code, retriable);
    }

    const data = (await response.json()) as TenantConfig;
    return data;
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * useTenantConfig — TanStack Query hook wrapper around fetchTenantConfig.
 *
 * The 5-minute staleTime matches the backend's Cache-Control header
 * (internal/tenant/tenant_config_handler.go). When the SPA switches
 * tenant (e.g. user navigates from acme.platform.example to
 * beta.platform.example), the cache key change ensures the new
 * tenant's config is fetched fresh.
 */
export function useTenantConfig(
  slug: string,
  apiBase = "/api",
): UseQueryResult<TenantConfig, TenantConfigError> {
  return useQuery<TenantConfig, TenantConfigError>({
    queryKey: tenantConfigQueryKey(slug),
    queryFn: () => fetchTenantConfig(slug, apiBase),
    enabled: slug !== "",
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, error) => {
      // Only retry on retriable errors. 404 / suspended / explicit
      // client errors are surfaced immediately.
      return error.retriable && failureCount < 3;
    },
    refetchOnWindowFocus: false,
  });
}
